import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { cloudflareSystemData } from './cloudflare-data';
import type {
  AdjustOcCreditInput,
  AuditFilters,
  CreateAdminUserInput,
  CreateFranchiseInput,
  CreateStoreInput,
  FranchiseFilters,
  IssueOcCreditInput,
  OcFilters,
  OcRequestReviewInput,
  OcRequestStatusFilter,
  ResetUserPasswordInput,
  SettingFilters,
  SetFranchiseStatusInput,
  StoreFilters,
  SuspendUserInput,
  UnsuspendUserInput,
  UpdateFranchiseInput,
  UpdateSystemSettingInput,
  UpdateStoreInput,
  UserFilters,
} from './data';

export function useSystemDashboardReadModel() {
  return useQuery({
    queryKey: ['system-admin', 'dashboard'],
    queryFn: cloudflareSystemData.dashboard,
  });
}

export function useCursorPagination() {
  const [cursor, setCursor] = useState<string | null>(null);
  return useMemo(
    () => ({
      cursor,
      reset: () => setCursor(null),
      goNext: (nextCursor: string | null) => setCursor(nextCursor),
    }),
    [cursor],
  );
}

export function useFranchisePage(filters: FranchiseFilters, cursor: string | null) {
  return useQuery({
    queryKey: ['system-admin', 'franchises', filters, cursor],
    queryFn: () => cloudflareSystemData.franchises(filters, cursor),
    placeholderData: (previousData) => previousData,
  });
}

export function useStorePage(filters: StoreFilters, cursor: string | null) {
  return useQuery({
    queryKey: ['system-admin', 'stores', filters, cursor],
    queryFn: () => cloudflareSystemData.stores(filters, cursor),
    placeholderData: (previousData) => previousData,
  });
}

export function useUserPage(filters: UserFilters, cursor: string | null) {
  return useQuery({
    queryKey: ['system-admin', 'users', filters, cursor],
    queryFn: () => cloudflareSystemData.users(filters, cursor),
    placeholderData: (previousData) => previousData,
  });
}

export function useOcTransactionPage(filters: OcFilters, cursor: string | null) {
  return useQuery({
    queryKey: ['system-admin', 'oc-transactions', filters, cursor],
    queryFn: () => cloudflareSystemData.ocTransactions(filters, cursor),
    placeholderData: (previousData) => previousData,
  });
}

export function useOcRequestPage(status: OcRequestStatusFilter, cursor: string | null) {
  return useQuery({
    queryKey: ['system-admin', 'oc-requests', status, cursor],
    queryFn: () => cloudflareSystemData.ocRequests(status, cursor),
    placeholderData: (previousData) => previousData,
  });
}

export function useAuditLogPage(filters: AuditFilters, cursor: string | null) {
  return useQuery({
    queryKey: ['system-admin', 'audit-logs', filters, cursor],
    queryFn: () => cloudflareSystemData.auditLogs(filters, cursor),
    placeholderData: (previousData) => previousData,
  });
}

export function useSettingPage(filters: SettingFilters) {
  return useQuery({
    queryKey: ['system-admin', 'settings', filters],
    queryFn: () => cloudflareSystemData.settings(filters),
  });
}

export function useActiveFranchiseOptions() {
  return useQuery({
    queryKey: ['system-admin', 'franchise-options', 'active'],
    queryFn: () => cloudflareSystemData.franchises({ status: 'active' }, null, 100),
  });
}

export function useStoreOptions() {
  return useQuery({
    queryKey: ['system-admin', 'store-options'],
    queryFn: () => cloudflareSystemData.stores({}, null, 100),
  });
}

function useSystemAdminMutation<TInput, TResult>(mutationFn: (input: TInput) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['system-admin'] });
    },
  });
}

export function useCreateFranchiseMutation() {
  return useSystemAdminMutation<CreateFranchiseInput, Awaited<ReturnType<typeof cloudflareSystemData.createFranchise>>>(
    cloudflareSystemData.createFranchise,
  );
}

export function useUpdateFranchiseMutation() {
  return useSystemAdminMutation<UpdateFranchiseInput, Awaited<ReturnType<typeof cloudflareSystemData.updateFranchise>>>(
    cloudflareSystemData.updateFranchise,
  );
}

export function useSetFranchiseStatusMutation() {
  return useSystemAdminMutation<SetFranchiseStatusInput, Awaited<ReturnType<typeof cloudflareSystemData.setFranchiseStatus>>>(
    cloudflareSystemData.setFranchiseStatus,
  );
}

export function useCreateStoreMutation() {
  return useSystemAdminMutation<CreateStoreInput, Awaited<ReturnType<typeof cloudflareSystemData.createStore>>>(
    cloudflareSystemData.createStore,
  );
}

export function useUpdateStoreMutation() {
  return useSystemAdminMutation<UpdateStoreInput, Awaited<ReturnType<typeof cloudflareSystemData.updateStore>>>(
    cloudflareSystemData.updateStore,
  );
}

export function useSuspendUserMutation() {
  return useSystemAdminMutation<SuspendUserInput, Awaited<ReturnType<typeof cloudflareSystemData.setUserStatus>>>(
    (input) => cloudflareSystemData.setUserStatus(input, 'suspended'),
  );
}

export function useUnsuspendUserMutation() {
  return useSystemAdminMutation<UnsuspendUserInput, Awaited<ReturnType<typeof cloudflareSystemData.setUserStatus>>>(
    (input) => cloudflareSystemData.setUserStatus(input, 'active'),
  );
}

export function useResetUserPasswordMutation() {
  return useSystemAdminMutation<ResetUserPasswordInput, Awaited<ReturnType<typeof cloudflareSystemData.resetPassword>>>(
    cloudflareSystemData.resetPassword,
  );
}

export function useCreateAdminUserMutation() {
  return useSystemAdminMutation<CreateAdminUserInput, Awaited<ReturnType<typeof cloudflareSystemData.createAdminUser>>>(
    cloudflareSystemData.createAdminUser,
  );
}

export function useIssueOcCreditMutation() {
  return useSystemAdminMutation<IssueOcCreditInput, Awaited<ReturnType<typeof cloudflareSystemData.issueOc>>>(
    cloudflareSystemData.issueOc,
  );
}

export function useApproveOcRequestMutation() {
  return useSystemAdminMutation<OcRequestReviewInput, Awaited<ReturnType<typeof cloudflareSystemData.approveOcRequest>>>(
    cloudflareSystemData.approveOcRequest,
  );
}

export function useRejectOcRequestMutation() {
  return useSystemAdminMutation<OcRequestReviewInput, Awaited<ReturnType<typeof cloudflareSystemData.rejectOcRequest>>>(
    cloudflareSystemData.rejectOcRequest,
  );
}

export function useAdjustOcCreditMutation() {
  return useSystemAdminMutation<AdjustOcCreditInput, Awaited<ReturnType<typeof cloudflareSystemData.adjustOc>>>(
    cloudflareSystemData.adjustOc,
  );
}

export function useUpdateSystemSettingMutation() {
  return useSystemAdminMutation<UpdateSystemSettingInput, Awaited<ReturnType<typeof cloudflareSystemData.updateSetting>>>(
    cloudflareSystemData.updateSetting,
  );
}
